import { editor, languages, Position, MarkerSeverity } from 'monaco-editor-core';
import { LuceneValidator } from './lucene.validator';
import { modelOptionsSchema } from './config.validator';
import { LUCENE_ID } from './constants';
import { LuceneModelOptions, LuceneModelOptionsMap, LuceneModelParsedOptions } from './interface';
import * as Parser from './lucene.parser';
import { LuceneCompletion } from './lucene.completion';
import { LanguageService } from '../../interface';

export class LuceneLanguageService implements LanguageService {
  private static instance?: LuceneLanguageService;
  private modelOptions: LuceneModelOptionsMap = {};
  private validation: LuceneValidator;
  private completion: LuceneCompletion;
  public triggerCharacters = ['"'];

  static getInstance(): LuceneLanguageService {
    if (!this.instance) {
      this.instance = new LuceneLanguageService();
    }
    return this.instance;
  }

  // singleton
  private constructor() {
    this.validation = LuceneValidator.getInstance();
    this.completion = LuceneCompletion.getInstance();
  }

  public registerModelOptions(model: editor.ITextModel, options: LuceneModelOptions) {
    const { value, error } = modelOptionsSchema.validate(options);
    if (error) {
      throw error;
    }

    this.modelOptions[model.uri.toString()] = value;
  }

  public unregisterModelOptions(model: editor.ITextModel) {
    delete this.modelOptions[model.uri.toString()];
  }

  private getOptions(model: editor.ITextModel): LuceneModelParsedOptions {
    let options = this.modelOptions[model.uri.toString()];
    if (!options) {
      options = {
        validate: true,
        hover: false,
        autoComplete: false,
      };
    }

    return options;
  }

  public async doValidate(model: editor.ITextModel) {
    const options = this.getOptions(model);
    if (!options.validate || !options.schema) {
      return true;
    }

    const markers: editor.IMarkerData[] = [];

    try {
      const value = model.getValue();
      const ast = Parser.parse(value, {});
      await this.validation.doValidate(ast, options.schema, markers);
    } catch (error) {
      markers.push({
        severity: MarkerSeverity.Error,
        startLineNumber: error.location.start.line,
        startColumn: error.location.start.column,
        endLineNumber: error.location.end.line,
        endColumn: error.location.end.column,
        message: error.message,
      });
    }

    editor.setModelMarkers(model, LUCENE_ID, markers);
    return markers.length === 0;
  }

  public async provideHover(model: editor.ITextModel, position: Position): Promise<languages.Hover | null> {
    const options = this.getOptions(model);
    if (!options.hover) {
      return null;
    }

    const items = [];
    // TODO: parse text and provide hover

    return { contents: items };
  }

  public async provideCompletionItems(
    model: editor.ITextModel,
    position: Position
  ): Promise<languages.CompletionList | null> {
    const options = this.getOptions(model);

    if (!options.autoComplete) {
      return null;
    }

    if (!options.schema) {
      return null;
    }

    const word = model.getWordUntilPosition(position);
    const range = {
      startLineNumber: position.lineNumber,
      endLineNumber: position.lineNumber,
      startColumn: word.startColumn,
      endColumn: word.endColumn,
    };

    const textUntilPosition = model.getValueInRange({
      startLineNumber: 1,
      startColumn: 1,
      endLineNumber: position.lineNumber,
      endColumn: position.column,
    });

    return this.completion.doComplete(textUntilPosition, range, options.schema);
  }

  public parse(value: string) {
    return Parser.parse(value);
  }
}
